import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/auth-context";
import SelfieImage from "./SelfiImage";
import { AlertCircle, Loader2 } from "lucide-react";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function FaceScanPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    token,
    fetchNearestCompany,
    cachedLocation,
    cacheLocation,
    cachedCompany,
    cacheCompany,
  } = useAuth();

  // Company may already be passed (e.g. after switching from QR → Face)
  const [company, setCompany] = useState(location.state?.company || null);
  const [resolving, setResolving] = useState(
    !location.state?.company && !cachedCompany
  );
  const [resolveError, setResolveError] = useState(null);

  // ── Resolve company once on mount ─────────────────────────────────────
  useEffect(() => {
    // Case A: came straight from SupervisorView with a freshly-resolved
    // company in route state — trust it, and refresh the cache so any
    // later loop redirect can reuse it without hitting the API again.
    if (location.state?.company) {
      setCompany(location.state.company);
      cacheCompany(location.state.company);
      setResolving(false);
      return;
    }

    if (company) return; // already resolved, nothing to do

    // Case B: redirected back here (no route-state company, e.g. after
    // marking attendance) — reuse the cached company, no API call.
    if (cachedCompany) {
      setCompany(cachedCompany);
      setResolving(false);
      return;
    }

    // Case C: cold start / deep link — nothing cached at all, resolve
    // from location same as before.
    const resolveFromLocation = async (loc) => {
      try {
        const data = await fetchNearestCompany({
          latitude: loc.latitude,
          longitude: loc.longitude,
        });
        const resolved = data?.company || data;
        const companyId = resolved?._id || resolved?.companyId;

        if (!companyId) {
          setResolveError("No nearby company found. Move closer and try again.");
        } else {
          const resolvedCompany = {
            _id: companyId,
            name: resolved.companyName,
            code: resolved.companyCode,
          };
          setCompany(resolvedCompany);
          cacheCompany(resolvedCompany);
        }
      } catch (err) {
        console.error("Nearest company fetch failed:", err);
        setResolveError("Failed to find nearby company. Please try again.");
      } finally {
        setResolving(false);
      }
    };

    if (cachedLocation?.latitude && cachedLocation?.longitude) {
      resolveFromLocation(cachedLocation);
      return;
    }

    if (!("geolocation" in navigator)) {
      setResolveError("Location is required to identify your company.");
      setResolving(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
        };
        cacheLocation(loc);
        resolveFromLocation(loc);
      },
      () => {
        setResolveError("Location access is required. Please enable it and reload.");
        setResolving(false);
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, cachedCompany, cachedLocation, fetchNearestCompany, cacheLocation, cacheCompany]);

  const handleSwitchToQr = () => {
    navigate("/lkart/admin/attendance", { state: { company } });
  };

  const handleSubmitFace = useCallback(
    async (blob) => {
      const formData = new FormData();
      formData.append("file", blob, "selfie.jpg");

      const response = await axios.post(
        `${BASE_URL}/lenskart/identify-user-by-face`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      const data = response.data;
      if (data?.success === false) {
        throw new Error(
          data.message || data.error || "Face not recognised. Please try again."
        );
      }

      const { userId, status, currentDate } = data?.data || {};
      if (!userId || !status || !currentDate) {
        throw new Error("Unexpected response from server.");
      }

      navigate("/lkart/admin/attendance", {
        state: {
          company,
          faceScanParams: {
            userId,
            currentDay: currentDate.split("T")[0],
            status,
            
          },
        },
      });
    },
    [navigate, token, company]
  );

  // ── Resolving company ─────────────────────────────────────────────────
  if (resolving) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center space-y-4">
          <Loader2 className="h-10 w-10 animate-spin text-indigo-600" />
          <div className="text-base font-medium text-gray-700">
            Checking nearby company...
          </div>
        </div>
      </div>
    );
  }

  // ── No company — show error ───────────────────────────────────────────
  if (!company) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white p-4">
        <div className="max-w-md w-full mx-4 p-6 bg-red-50 rounded-lg shadow-sm">
          <div className="text-red-600 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Error</h3>
            <p>{resolveError || "No company found. Please try again."}</p>
            <button
              onClick={() => navigate("/lkart/admin/supervisor")}
              className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <SelfieImage onSubmit={handleSubmitFace} onClose={handleSwitchToQr} />;
}